// Route Code Edited 10/22/2026
/**
* route-i18n.js — Multi-language text localization for the Route "Preferred Checkout"
* cart widget (App Embed / v3). Build: HEROISMO (heroismo-apparel.myshopify.com).
* Supports: English (no-op), French (approved), German (DRAFT — pending native review).
* Theme-only; no changes to the Route widget codebase.
*
* HEROISMO NOTES (confirmed via injection on live site 10/20/2026)
*   - Snappy v3 App Embed, Paired layout, checkoutSelector=button[name=checkout], app_embedded=true.
*   - Published storefronts: en, en-BE, en-NL, en-DK, en-US, en-GB, fr-FR, de-DE.
*   - /fr-fr/ -> Shopify.locale "fr"; /de-de/ -> Shopify.locale "de"; en-* markets stay English.
*   - FR + DE render the STANDARD (shipping-protection) variant (not Returns — FR/DE aren't eligible).
*   - No ET customizations (CTA "Checkout", CWC "Continue without package protection",
*     premium "Package Protection") — all covered by baked-in tables, so IE_CUSTOM is empty.
*   - No cashback preset; patterns path is a no-op.
*   - Spanish/Portuguese tables removed (store only publishes en/fr/de).
*
* GERMAN STATUS
*   - UI strings are DRAFT and need native-speaker review before go-live (informal "du" to match
*     the store's own German, e.g. "Dein Warenkorb").
*   - Legal/disclaimer strings are DRAFT translations kept inline for the reviewer's convenience.
*     ==> DO NOT go live on German until Legal + a qualified translator sign off on the disclaimer.
*   - "See Details" expander: rendered as two nodes ("See" + "Details"). To produce "Details ansehen"
*     idempotently, the first node is blanked ("See":"") and the full phrase is placed on the second
*     ("Details":"Details ansehen"). Confirm adjacency/order in QA with ?route_i18n_debug=1.
*
* WHY THIS EXISTS
*   Route's cart widget renders only in English. Its visible copy is not overridable via theme
*   config (it comes from Route's API through React props). This script rewrites the widget's
*   rendered English text nodes into the storefront language on non-English storefronts only.
*
* DESIGN (read before editing)
*   - HYBRID MATCHING: exact content matching (normalized English -> translation) for widget copy;
*     a PATTERN path for strings with an inline dynamic value; dedicated BUTTON handling for the
*     EQ checkout CTA, the premium line, and the CWC link.
*   - EQ BUTTON TEXT IS TRANSLATED (not copied) via dictionaries; unmatched strings stay English
*     (safe) and are flagged by the drift logger.
*   - STATELESS / IDEMPOTENT: no "translated" marker is set on the DOM. Re-passes are safe because
*     a translated value is never itself a key. INVARIANT: within each (merged) locale map, no value
*     may equal a key.
*   - WIDGET VARIANTS: both RETURNS and STANDARD variants are covered per locale; missing strings
*     fall back to English.
*
* IE CUSTOMIZATION
*   - The EQ checkout CTA, CWC link, and premium-line subtext are ET-customizable per merchant.
*     Add any merchant-specific strings to IE_CUSTOM below (tried BEFORE baked-in; win on collision).
*   - Heroismo needs none — its strings are all baked-in defaults.
*
* SCOPE & SAFETY (must be preserved on any edit)
*   - Rewrites text-node values and aria-labels inside Route-owned elements, plus the label text in
*     the cloned button's stacked spans / CWC link. Never adds/removes elements, never changes button
*     attributes/handlers/type/structure, never clicks, never calls Route.
*   - Fail-safe: every entry point is wrapped in try/catch. Any error leaves the original English
*     rendering functional. Removing this file reverts behavior completely.
*   - Amounts/number formatting are left to Route (hard-coded en-US — a Route-side bug this script
*     does not attempt to fix).
*
* DEPLOYMENT
*   - Load via theme.liquid on non-English storefronts:
*       {%- if localization.language.iso_code != 'en' -%}
*         <script src="{{ 'route-i18n-handler.js' | asset_url }}" defer></script>
*       {%- endif -%}
*   - Text-expansion/layout for longer FR/DE strings is handled with CSS in the Route Theme App
*     Embed (scoped to data-route-* attributes), not in this file. DE runs longer than FR — QA it.
*/
(function () {
  "use strict";

  var DEBOUNCE_MS = 50;

  // Sentinel for "key exists but no approved translation yet" => leave English, log in debug.
  var TODO = null;

  // ===========================================================================================
  // ============================  IE CONFIG — EDIT ONLY THIS BLOCK  ============================
  // Per-merchant Employee Tools customizations for the checkout CTA, CWC link, and premium subtext.
  // Keyed by the rendered English string; provide each supported language (fr, de).
  //   - Tried BEFORE the baked-in common variations; wins on a collision.
  //   - checkout/cwc matching is case-insensitive + whitespace-normalized.
  //   - `premium` entries are the SUBTEXT only; the amount is preserved automatically.
  // Heroismo: none required (all strings are baked-in defaults).
  // Find custom strings by previewing with ?route_i18n_debug=1 and reading the console.
  // ===========================================================================================
  var IE_CUSTOM = {
    checkout: {
      // "Secure Checkout": { fr: "Paiement sécurisé", de: "Sicher zur Kasse" },
    },
    cwc: {
      // "No thanks, continue": { fr: "Non merci, continuer", de: "Nein danke, weiter" },
    },
    premium: {
      // "Shipping Protection": { fr: "Protection de l'expédition", de: "Versandschutz" },
    }
  };
  // ==========================  END IE CONFIG  ================================================

  // ===========================================================================================
  // BAKED-IN LOCALE TABLES
  // Each locale: { text, aria, patterns, buttons: { checkout, cwc }, premium: [{en, tr}] }
  // ===========================================================================================
  var LOCALES = {

    // English storefront is a no-op; tables intentionally empty.
    en: { text: {}, aria: {}, patterns: [], buttons: { checkout: {}, cwc: {} }, premium: [] },

    // ---------------------------------------------------------------------------------------
    // FRENCH — Approved.
    // ---------------------------------------------------------------------------------------
    fr: {
      text: {
        // RETURNS variant
        "Free Returns and your order is protected": "Retours gratuits et votre commande est protégée",
        "for": "pour",
        "See what's included": "Voir ce qui est inclus",
        "BENEFITS:": "AVANTAGES :",
        "Free return shipping and no restocking fees": "Expédition de retour gratuite et sans frais de réapprovisionnement",
        "Free return shipping and": "Expédition de retour gratuite et",
        "no restocking fees": "sans frais de réapprovisionnement",
        "Damage, loss, theft coverage": "Couverture en cas de dommages, de perte ou de vol",
        "Fast refund or replacement": "Remboursement ou remplacement rapide",
        "See": "Voir",
        "Details": "Détails",
        // Modal — OFFICIAL (verbatim fr.json)
        "We've got you covered.": "Nous vous avons couvert.",
        "Package Protection": "Protection colis",
        "Coverage in case your package is damaged in transit, stolen, or doesn't arrive.": "Couverture au cas où votre colis est endommagé en transit, volé, ou n'arrive pas.",
        "Instant Issue Resolution": "Résolution instantanée des problèmes",
        "Get a refund or replacement in just a few clicks.": "Obtenez un remboursement ou un remplacement en quelques clics.",
        "24/7 Claim Support": "Support de réclamation 24/7",
        "Route is here for you whenever an issue arises — with an average response time of under an hour.": "Route est là pour vous chaque fois qu'un problème survient — avec un temps de réponse moyen de moins d'une heure.",
        "File A Claim": "Déposer une réclamation",
        "User Privacy": "Confidentialité de l'utilisateur",
        "Terms of Service": "Conditions de service",
        // Modal — DRAFT (composed; pending approval)
        "Exchanges and Returns": "Échanges et retours",
        "With Route Package protection, you get return shipping and restocking fees covered.": "Avec la protection colis Route, les frais d'expédition de retour et de réapprovisionnement sont couverts.",
        "Free Returns and Route Package Protection saves you time and money.": "Les retours gratuits et la protection colis Route vous font gagner du temps et de l'argent.",
        // Legal — RETURNS variant
        "Route package protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery, where Route traces the transportation. Route App, Inc. (Route) is the named insured on the shipping insurance policy (Policy); Route Insurance Services (RIS), Route's licensed producer entity, procures the policy from SEG Insurance Ltd. Route, through RIS, may receive compensation for its services. A portion of the amount collected by Route is sent to the merchant to cover returns.": "La protection colis Route étend la couverture pour les achats en ligne qui sont perdus ou endommagés en transit, ou volés immédiatement après la preuve de livraison du transporteur où Route trace le transport. Route App, Inc. (Route) est l'assuré nommé sur la police d'assurance expédition (« Police ») ; Route Insurance Services (« RIS »), l'entité productrice agréée de Route, obtient la police auprès de SEG Insurance Ltd. Route, par l'intermédiaire de RIS, peut recevoir une compensation pour ses services. Une partie du montant collecté par Route est versée au marchand pour couvrir les retours.",

        // STANDARD / shipping-protection variant
        "Your order is protected for": "Votre commande est protégée pour",
        "Your order is protected.": "Votre commande est protégée.",
        "Order protected for": "Commande protégée pour",
        "Package": "Protection colis",     // fragment 1 (see SPLIT-NODE note)
        "Protection by": "par",                // fragment 2 -> "Protection colis par <logo>"
        "Protection": "",                      // modal bullet title splits "Package Protection" into "Package"+" Protection"; blank node 2 (node 1 carries the full name) -> "Protection colis"
        "Shipping Protection gives you peace of mind while saving you time and money.": "La protection de l'expédition vous offre tranquillité d'esprit tout en vous faisant gagner du temps et de l'argent.",
        "Package Protection gives you peace of mind while saving you time and money.": "La protection colis vous offre tranquillité d'esprit tout en vous faisant gagner du temps et de l'argent.",
        // Legal — SHIPPING variant (DRAFT, LEGALLY SENSITIVE; pending Route/Legal sign-off)
        "Route shipping protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery where Route traces the transportation. Route App, Inc. (Route) is the named insured on the shipping insurance policy (Policy); Route Insurance Solutions (RIS), Route's licensed producer entity, procures the Policy from SEG Insurance Ltd. Route, through RIS, and may receive compensation for its services.": "La protection d'expédition Route étend la couverture pour les achats en ligne qui sont perdus ou endommagés en transit, ou volés immédiatement après la preuve de livraison du transporteur où Route trace le transport. Route App, Inc. (Route) est l'assuré nommé sur la police d'assurance expédition (« Police ») ; Route Insurance Services (« RIS »), l'entité productrice agréée de Route, obtient la police auprès de SEG Insurance Ltd. Route, par l'intermédiaire de RIS, peut recevoir une compensation pour ses services.",
        "Route package protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery where Route traces the transportation. Route App, Inc. (Route) is the named insured on the package insurance policy (Policy); Route Insurance Solutions (RIS), Route's licensed producer entity, procures the Policy from SEG Insurance Ltd. Route, through RIS, and may receive compensation for its services.": "La protection colis Route étend la couverture aux achats en ligne qui sont perdus ou endommagés en cours de transport, ou volés immédiatement après la preuve de livraison du transporteur pour lesquels Route assure le suivi du transport. Route App, Inc. (Route) est l'assuré désigné sur la police d'assurance colis (« Police ») ; Route Insurance Services (« RIS »), l'entité productrice autorisée de Route, souscrit la Police auprès de SEG Insurance Ltd. Route, par l'intermédiaire de RIS, peut recevoir une rémunération pour ses services."
      },
      aria: {
        "route shipping protection selector element": "Sélecteur de protection d'expédition Route",
        "Coverage Options": "Options de couverture",
        "See what's included": "Voir ce qui est inclus",
        "Show package protection benefits": "Afficher les avantages de la protection colis", // added 10/20/2026 (observed on Heroismo)
        "Hide package protection benefits": "Masquer les avantages de la protection colis",  // expanded-state toggle
        "Checkout Without Coverage": "Passer à la caisse sans protection"
      },
      buttons: {
        checkout: {
          "Checkout": "Passer Commande",
          "Check Out": "Passer Commande",
          "Proceed to Checkout": "Procéder au paiement",
          "Continue to Checkout": "Continuer vers le paiement",
          "Continue": "Continuer",
          "Protected Checkout": "Paiement protégé",
          "Checkout with Coverage": "Payer avec couverture",
          "Check Out with Coverage": "Payer avec couverture",
          "Checkout with Protection": "Payer avec protection",
          "Check Out with Protection": "Payer avec protection",
          "Checkout with Package Protection": "Payer avec protection colis",
          "Checkout with Shipping Protection": "Payer avec protection d'expédition",
          "Checkout with Shipment Protection": "Payer avec protection d'expédition",
          "Checkout with Route Protection": "Payer avec protection Route",
          "Checkout with Route Coverage": "Payer avec protection Route"
        },
        cwc: {
          "Checkout without Coverage": "Payer sans couverture",
          "Check Out without Coverage": "Payer sans couverture",
          "Checkout without Protection": "Payer sans protection",
          "Checkout without Package Protection": "Payer sans protection colis",
          "Continue without Package Protection": "Continuer sans protection colis",
          "Continue without Protection": "Continuer sans protection",
          "Continue without Coverage": "Continuer sans couverture",
          "Proceed without Package Protection": "Continuer sans protection colis",
          "Checkout without Shipping Protection": "Payer sans protection d'expédition",
          "Checkout without Shipment Protection": "Payer sans protection d'expédition",
          "Checkout without Free Returns": "Payer sans retours gratuits",
          "Checkout without Free Return": "Payer sans retour gratuit",
          "Continue without Insurance": "Continuer sans assurance",
          "Checkout without Order Protection": "Payer sans protection de commande",
          "Checkout without Route Protection": "Payer sans protection Route",
          "Checkout without Route Coverage": "Payer sans protection Route",
          "Checkout Without Add-on Protection": "Payer sans protection complémentaire",
          "Or continue without package protection": "Ou continuer sans protection colis"
        }
      },
      patterns: [
        {
          re: /^Earn (.+?) back on your item total — sent to your inbox after purchase to use on your next order\. Cash Back is provided by the merchant\.$/,
          tr: "Gagnez $1 sur le total de vos articles — envoyé dans votre boîte mail après l'achat à utiliser sur votre prochaine commande. Le cashback est fourni par le marchand."
        }
      ],
      premium: [
        { en: "Free Returns and Package Protection", tr: "Retours gratuits et protection colis" }, // DRAFT
        { en: "Package Protection and Cash Back", tr: "Protection colis et Cashback" },             // DRAFT
        { en: "Package Protection", tr: "Protection colis" },                                       // approved
        { en: "Shipment Protection", tr: "Protection d'expédition" }                                    // from ET data
      ]
    },

    // ---------------------------------------------------------------------------------------
    // GERMAN — de (de-DE). UI strings DRAFT (need native review). Informal "du" form.
    // Legal strings are DRAFT translations kept inline for reviewer convenience — DO NOT ship
    // German live until Legal + a qualified translator sign off on the disclaimer text.
    // ---------------------------------------------------------------------------------------
    de: {
      text: {
        // RETURNS variant (not shown on FR/DE markets today; included for completeness)
        "Free Returns and your order is protected": "Kostenlose Rücksendungen und deine Bestellung ist geschützt",
        "Free return shipping and no restocking fees": "Kostenloser Rückversand und keine Wiedereinlagerungsgebühren",
        "Free return shipping and": "Kostenloser Rückversand und",
        "no restocking fees": "keine Wiedereinlagerungsgebühren",

        // STANDARD / shipping-protection variant — WHAT HEROISMO RENDERS
        "Your order is protected.": "Deine Bestellung ist geschützt.",
        "Your order is protected for": "Deine Bestellung ist geschützt für",
        "Order protected for": "Bestellung geschützt für",
        "for": "für",
        "See what's included": "Was ist enthalten?",
        "BENEFITS:": "VORTEILE:",
        // "See Details" expander renders as two nodes ("See" + "Details"). Blank the first node and
        // place the full phrase on the second so it reads "Details ansehen" idempotently.
        "See": "",
        "Details": "Details ansehen",
        "Package": "Paketschutz",            // split-node fragment 1
        "Protection by": "von",              // fragment 2 -> "Paketschutz von <logo>"
        "Protection": "",                    // modal bullet title splits "Package Protection" into "Package"+" Protection"; blank node 2 (node 1 carries the full name) -> "Paketschutz"
        "Damage, loss, theft coverage": "Schutz bei Beschädigung, Verlust und Diebstahl",
        "Fast refund or replacement": "Schnelle Rückerstattung oder Ersatz",

        // Modal ("See what's included")
        "We've got you covered.": "Du bist rundum abgesichert.",
        "Package Protection": "Paketschutz",
        "Coverage in case your package is damaged in transit, stolen, or doesn't arrive.": "Schutz, falls dein Paket auf dem Transportweg beschädigt oder gestohlen wird oder nicht ankommt.",
        "Instant Issue Resolution": "Sofortige Problemlösung",
        "Get a refund or replacement in just a few clicks.": "Erhalte eine Rückerstattung oder Ersatz mit nur wenigen Klicks.",
        "24/7 Claim Support": "24/7-Schadenssupport",
        "Route is here for you whenever an issue arises — with an average response time of under an hour.": "Route ist für dich da, wann immer ein Problem auftritt – mit einer durchschnittlichen Antwortzeit von unter einer Stunde.",
        "File A Claim": "Schaden melden",
        "User Privacy": "Datenschutz",
        "Terms of Service": "Nutzungsbedingungen",
        "Exchanges and Returns": "Umtausch und Rücksendungen",
        "With Route Package protection, you get return shipping and restocking fees covered.": "Mit dem Paketschutz von Route sind Rückversand und Wiedereinlagerungsgebühren abgedeckt.",
        "Free Returns and Route Package Protection saves you time and money.": "Kostenlose Rücksendungen und der Paketschutz von Route sparen dir Zeit und Geld.",
        "Shipping Protection gives you peace of mind while saving you time and money.": "Der Versandschutz gibt dir Sicherheit und spart dir gleichzeitig Zeit und Geld.",
        "Package Protection gives you peace of mind while saving you time and money.": "Der Paketschutz gibt dir Sicherheit und spart dir gleichzeitig Zeit und Geld.",

        // Legal — DRAFT translations, LEGALLY SENSITIVE; pending Legal + qualified-translator sign-off.
        // The FIRST key is the one Heroismo actually renders (standard/package variant).
        "Route package protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery where Route traces the transportation. Route App, Inc. (Route) is the named insured on the package insurance policy (Policy); Route Insurance Solutions (RIS), Route's licensed producer entity, procures the Policy from SEG Insurance Ltd. Route, through RIS, and may receive compensation for its services.": "Der Paketschutz von Route erweitert den Versicherungsschutz für Online-Einkäufe, die auf dem Transportweg verloren gehen oder beschädigt werden, oder die unmittelbar nach dem Zustellnachweis des Versanddienstleisters gestohlen werden, sofern Route die Beförderung nachverfolgt. Route App, Inc. (Route) ist der benannte Versicherungsnehmer in der Paketversicherungspolice (Police); Route Insurance Solutions (RIS), das lizenzierte Vermittlerunternehmen von Route, bezieht die Police von SEG Insurance Ltd. Route kann über RIS eine Vergütung für seine Dienstleistungen erhalten.",
        "Route shipping protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery where Route traces the transportation. Route App, Inc. (Route) is the named insured on the shipping insurance policy (Policy); Route Insurance Solutions (RIS), Route's licensed producer entity, procures the Policy from SEG Insurance Ltd. Route, through RIS, and may receive compensation for its services.": "Der Versandschutz von Route erweitert den Versicherungsschutz für Online-Einkäufe, die auf dem Transportweg verloren gehen oder beschädigt werden, oder die unmittelbar nach dem Zustellnachweis des Versanddienstleisters gestohlen werden, sofern Route die Beförderung nachverfolgt. Route App, Inc. (Route) ist der benannte Versicherungsnehmer in der Transportversicherungspolice (Police); Route Insurance Solutions (RIS), das lizenzierte Vermittlerunternehmen von Route, bezieht die Police von SEG Insurance Ltd. Route kann über RIS eine Vergütung für seine Dienstleistungen erhalten.",
        "Route package protection extends coverage for online purchases that are lost or damaged in transit, or stolen immediately after the carrier's proof of delivery, where Route traces the transportation. Route App, Inc. (Route) is the named insured on the shipping insurance policy (Policy); Route Insurance Services (RIS), Route's licensed producer entity, procures the policy from SEG Insurance Ltd. Route, through RIS, may receive compensation for its services. A portion of the amount collected by Route is sent to the merchant to cover returns.": "Der Paketschutz von Route erweitert den Versicherungsschutz für Online-Einkäufe, die auf dem Transportweg verloren gehen oder beschädigt werden, oder die unmittelbar nach dem Zustellnachweis des Versanddienstleisters gestohlen werden, sofern Route die Beförderung nachverfolgt. Route App, Inc. (Route) ist der benannte Versicherungsnehmer in der Transportversicherungspolice (Police); Route Insurance Services (RIS), das lizenzierte Vermittlerunternehmen von Route, bezieht die Police von SEG Insurance Ltd. Route kann über RIS eine Vergütung für seine Dienstleistungen erhalten. Ein Teil des von Route eingezogenen Betrags wird an den Händler überwiesen, um Rücksendungen abzudecken."
      },
      aria: {
        "route shipping protection selector element": "Route-Versandschutz-Auswahl",
        "Coverage Options": "Schutzoptionen",
        "See what's included": "Was ist enthalten?",
        "Show package protection benefits": "Paketschutz-Vorteile anzeigen",
        "Hide package protection benefits": "Paketschutz-Vorteile ausblenden",   // expanded-state toggle
        "Checkout Without Coverage": "Ohne Schutz zur Kasse"
      },
      buttons: {
        checkout: {
          "Checkout": "Zur Kasse",
          "Check Out": "Zur Kasse",
          "Proceed to Checkout": "Weiter zur Kasse",
          "Continue to Checkout": "Weiter zur Kasse",
          "Continue": "Weiter",
          "Protected Checkout": "Geschützt zur Kasse",
          "Checkout with Coverage": "Mit Schutz zur Kasse",
          "Check Out with Coverage": "Mit Schutz zur Kasse",
          "Checkout with Protection": "Mit Schutz zur Kasse",
          "Check Out with Protection": "Mit Schutz zur Kasse",
          "Checkout with Package Protection": "Mit Paketschutz zur Kasse",
          "Checkout with Shipping Protection": "Mit Versandschutz zur Kasse",
          "Checkout with Shipment Protection": "Mit Versandschutz zur Kasse",
          "Checkout with Route Protection": "Mit Route-Schutz zur Kasse",
          "Checkout with Route Coverage": "Mit Route-Schutz zur Kasse"
        },
        cwc: {
          "Checkout without Coverage": "Ohne Schutz zur Kasse",
          "Check Out without Coverage": "Ohne Schutz zur Kasse",
          "Checkout without Protection": "Ohne Schutz zur Kasse",
          "Checkout without Package Protection": "Ohne Paketschutz zur Kasse",
          "Continue without Package Protection": "Weiter ohne Paketschutz",
          "Continue without Protection": "Weiter ohne Schutz",
          "Continue without Coverage": "Weiter ohne Schutz",
          "Proceed without Package Protection": "Weiter ohne Paketschutz",
          "Checkout without Shipping Protection": "Ohne Versandschutz zur Kasse",
          "Checkout without Shipment Protection": "Ohne Versandschutz zur Kasse",
          "Checkout without Free Returns": "Ohne kostenlose Rücksendungen zur Kasse",
          "Checkout without Free Return": "Ohne kostenlose Rücksendung zur Kasse",
          "Continue without Insurance": "Weiter ohne Versicherung",
          "Checkout without Order Protection": "Ohne Bestellschutz zur Kasse",
          "Checkout without Route Protection": "Ohne Route-Schutz zur Kasse",
          "Checkout without Route Coverage": "Ohne Route-Schutz zur Kasse",
          "Checkout Without Add-on Protection": "Ohne Zusatzschutz zur Kasse",
          "Or continue without package protection": "Oder weiter ohne Paketschutz"
        }
      },
      patterns: [], // no cashback preset on Heroismo
      premium: [ // DRAFT — native review. Heroismo renders "Package Protection".
        { en: "Free Returns and Package Protection", tr: "Kostenlose Rücksendungen und Paketschutz" },
        { en: "Package Protection and Cash Back", tr: "Paketschutz und Cashback" },
        { en: "Package Protection", tr: "Paketschutz" },
        { en: "Shipment Protection", tr: "Versandschutz" }
      ]
    }
  };

  // --- Route-owned root selectors (scoping). Modal is a body-level portal, list it explicitly.
  var SELECTORS = [
    "[data-route-widget]",
    "[data-route-widget-header]",
    "[data-route-widget-cwc]",
    "[data-checkout-without-coverage-link]",
    "[data-route-cloned-button]",
    '[data-testid="route-modal"]',
    ".route-modal"
  ].join(",");

  // Elements owned by the dedicated button handler (translateButtons); the generic text pass
  // skips them to avoid double-handling and drift-logger noise.
  var BUTTON_OWNED_SELECTOR = ".route-checkout-text-stacked, .route-premium-text-stacked, [data-checkout-without-coverage-link]";

  // ===========================================================================================
  // ENGINE
  // ===========================================================================================

  function resolveLocale() {
    try {
      var raw = (window.Shopify && Shopify.locale) || document.documentElement.lang || "";
      raw = String(raw).toLowerCase();
      if (!raw && /^\/en(\/|$)/.test(location.pathname)) raw = "en";
      var base = raw.split("-")[0];
      return (base === "fr" || base === "de" || base === "en") ? base : "en";
    } catch (e) {
      return "en";
    }
  }

  function isDebug() {
    try {
      return /[?&]route_i18n_debug=1(\b|&|$)/.test(location.search)
        || (window.Shopify && Shopify.designMode === true)
        || /[?&]preview_theme_id=/.test(location.search)
        || window.__ROUTE_I18N_DEBUG__ === true;
    } catch (e) {
      return false;
    }
  }

  var LOCALE = resolveLocale();
  if (LOCALE === "en" || !LOCALES[LOCALE]) return; // English / unsupported => no-op

  var DEBUG = isDebug();
  var TABLE = LOCALES[LOCALE];
  var TEXT = new Map(Object.entries(TABLE.text || {}));
  var ARIA = new Map(Object.entries(TABLE.aria || {}));
  var PATTERNS = TABLE.patterns || [];

  function norm(s) {
    return String(s)
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/\s+/g, " ")
      .trim();
  }

  // --- Merge IE_CUSTOM (per-merchant) over baked-in defaults for the active locale. -----------
  function ieExactMap(slot) {
    var src = (IE_CUSTOM && IE_CUSTOM[slot]) || {};
    var out = {};
    Object.keys(src).forEach(function (en) {
      var t = src[en] && src[en][LOCALE];
      if (t != null && t !== "") out[en] = t;
    });
    return out;
  }
  function iePremiumList() {
    var src = (IE_CUSTOM && IE_CUSTOM.premium) || {};
    var arr = [];
    Object.keys(src).forEach(function (en) {
      var t = src[en] && src[en][LOCALE];
      if (t != null && t !== "") arr.push({ en: en, tr: t });
    });
    return arr;
  }

  // Button maps are matched CASE-INSENSITIVELY (keys lowercased) + whitespace-normalized.
  function toCiMap(obj) {
    var m = new Map();
    Object.keys(obj).forEach(function (k) {
      var v = obj[k];
      if (v != null && v !== "") m.set(norm(k).toLowerCase(), v);
    });
    return m;
  }

  var bakedButtons = TABLE.buttons || { checkout: {}, cwc: {} };
  var CHECKOUT = toCiMap(Object.assign({}, bakedButtons.checkout || {}, ieExactMap("checkout")));
  var CWC = toCiMap(Object.assign({}, bakedButtons.cwc || {}, ieExactMap("cwc")));
  var PREMIUM = (function () {
    var combined = iePremiumList().concat(TABLE.premium || []);
    var seen = {}, out = [];
    combined.forEach(function (s) {
      if (s && s.en != null && s.tr != null && !Object.prototype.hasOwnProperty.call(seen, s.en)) {
        seen[s.en] = 1; out.push(s);
      }
    });
    out.sort(function (a, b) { return b.en.length - a.en.length; });
    return out;
  })();

  // Reverse set of translated values so the drift logger doesn't flag already-translated text.
  var VALUE_SET = new Set();
  function _collectValues(m) { m.forEach(function (v) { if (v != null) VALUE_SET.add(v); }); }
  _collectValues(TEXT); _collectValues(CHECKOUT); _collectValues(CWC);
  PREMIUM.forEach(function (s) { if (s && s.tr != null) VALUE_SET.add(s.tr); });

  // Idempotency invariant check (debug only): no translated value may also be a key.
  if (DEBUG) {
    try {
      function _chk(map, label) {
        map.forEach(function (v) {
          if (v != null && map.has(v)) {
            console.warn("[route-i18n] IDEMPOTENCY RISK (" + LOCALE + ", " + label + "): value is also a key:", JSON.stringify(v));
          }
        });
      }
      _chk(TEXT, "text"); _chk(CHECKOUT, "buttons.checkout"); _chk(CWC, "buttons.cwc");
      PREMIUM.forEach(function (s) {
        if (s && s.tr != null && s.tr === s.en) {
          console.warn("[route-i18n] IDEMPOTENCY RISK (" + LOCALE + ", premium): tr equals en:", JSON.stringify(s.en));
        }
      });
    } catch (e) { /* ignore */ }
  }

  var _logged = new Set();
  function logDrift(kind, key) {
    if (!DEBUG || _logged.has(kind + key)) return;
    _logged.add(kind + key);
    console.warn("[route-i18n] " + kind + " (" + LOCALE + "): " + JSON.stringify(key));
  }

  function writeNode(node, raw, value) {
    var lead = raw.match(/^\s*/)[0];
    var trail = raw.match(/\s*$/)[0];
    var next = lead + value + trail;
    if (node.nodeValue !== next) node.nodeValue = next;
  }

  function translateTextNode(node) {
    // Button/CWC spans are handled by translateButtons(); skip them in the generic pass.
    if (node.parentElement && node.parentElement.closest(BUTTON_OWNED_SELECTOR)) return;

    var raw = node.nodeValue;
    var key = norm(raw);
    if (!key) return;

    if (TEXT.has(key)) {
      var val = TEXT.get(key);
      if (val == null) { logDrift("TODO untranslated key", key); return; } // leave English
      writeNode(node, raw, val);
      return;
    }

    for (var i = 0; i < PATTERNS.length; i++) {
      var p = PATTERNS[i];
      if (!p || p.tr == null) continue;
      if (p.re.test(key)) { writeNode(node, raw, key.replace(p.re, p.tr)); return; }
    }

    if (/[A-Za-z]/.test(key) && !VALUE_SET.has(key)) logDrift("Unknown string (drift?)", key);
  }

  function translateAriaLabel(el) {
    if (!el || el.nodeType !== 1 || !el.hasAttribute("aria-label")) return;
    var key = norm(el.getAttribute("aria-label"));
    if (!ARIA.has(key)) return;
    var value = ARIA.get(key);
    if (value != null && el.getAttribute("aria-label") !== value) el.setAttribute("aria-label", value);
  }

  function translateWithin(root) {
    try {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      for (var node = walker.nextNode(); node; node = walker.nextNode()) translateTextNode(node);
      translateAriaLabel(root);
      root.querySelectorAll("[aria-label]").forEach(translateAriaLabel);
    } catch (e) { /* fail-safe */ }
  }

  // --- Dedicated button handling (checkout CTA, CWC link, premium subtext) --------------------
  function setSingleTextNode(el, value) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var only = null, count = 0;
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeValue.trim()) { if (!only) only = n; count++; }
    }
    if (only && count === 1) { if (norm(only.nodeValue) !== norm(value)) only.nodeValue = value; return; }
    if (norm(el.textContent) !== norm(value)) el.textContent = value; // fallback
  }

  function translateButtonEl(el, map, kind) {
    if (!el) return;
    var current = norm(el.textContent || "");
    if (!current) return;
    var key = current.toLowerCase(); // maps are case-insensitive
    if (map.has(key)) { var v = map.get(key); if (v != null) setSingleTextNode(el, v); return; }
    if (DEBUG && !VALUE_SET.has(current) && /[A-Za-z]/.test(current)) logDrift(kind + " not matched", current);
  }

  function translatePremiumSpan(span) {
    var wrote = false;
    var walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT, null);
    for (var node = walker.nextNode(); node && !wrote; node = walker.nextNode()) {
      var raw = node.nodeValue, normed = norm(raw);
      if (!normed) continue;
      var nl = normed.toLowerCase();
      for (var i = 0; i < PREMIUM.length; i++) {
        var s = PREMIUM[i];
        if (!s || s.tr == null) continue;
        var en = s.en.toLowerCase();
        if (nl === en) { writeNode(node, raw, s.tr); wrote = true; break; }
        if (nl.length > en.length
            && nl.slice(-en.length) === en
            && nl.charAt(nl.length - en.length - 1) === " ") {
          writeNode(node, raw, normed.slice(0, normed.length - en.length) + s.tr);
          wrote = true; break;
        }
      }
    }
    if (wrote || !DEBUG) return;
    var full = norm(span.textContent);
    if (!/[A-Za-z]/.test(full)) return;
    for (var j = 0; j < PREMIUM.length; j++) {
      var t = PREMIUM[j] && PREMIUM[j].tr;
      if (t && (full === t || (full.length >= t.length && full.slice(-t.length) === t))) return; // already translated
    }
    logDrift("Premium subtext not matched (custom ET subtext?)", full);
  }

  function translateButtons() {
    try {
      document.querySelectorAll("[data-route-cloned-button]").forEach(function (clone) {
        translateButtonEl(clone.querySelector(".route-checkout-text-stacked"), CHECKOUT, "Checkout CTA");
        var pText = clone.querySelector(".route-premium-text-stacked");
        if (pText) translatePremiumSpan(pText);
      });
      document.querySelectorAll("[data-checkout-without-coverage-link]").forEach(function (el) {
        translateButtonEl(el, CWC, "CWC link");
      });
    } catch (e) { /* fail-safe */ }
  }

  function translateAll() {
    try {
      document.querySelectorAll(SELECTORS).forEach(translateWithin);
      translateButtons();
    } catch (e) { /* fail-safe */ }
  }

  // --- Observer & bootstrap ------------------------------------------------------------------
  var pending = null;
  function scheduleTranslate() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; translateAll(); }, DEBOUNCE_MS);
  }

  function start() {
    try {
      translateAll();
      new MutationObserver(scheduleTranslate).observe(document.body, {
        childList: true, subtree: true, characterData: true
      });
      if (DEBUG) console.info("[route-i18n] active — locale:", LOCALE);
    } catch (e) { /* fail-safe */ }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
// End Route Code